"use client";
import TestimonialCard from "@/components/ui/TestimonialCard";
import TestimonialModal from "@/components/ui/TestimonialModal";
import { ExtendedTestimonial } from "@/data/testimonials";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";

// Database testimonial type
type DbTestimonial = {
    _id: string;
    clientName: string;
    clientDesignation: string;
    clientCompany: string;
    clientCompanyLogo: string;
    rating: number;
    review: string;
    projectLink?: string;
    hasProjectLink?: boolean;
    avatar?: string;
    platformLogo?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

// Helper function to convert database testimonial to ExtendedTestimonial format
const convertDbToExtendedTestimonial = (t: DbTestimonial): ExtendedTestimonial => {
    return {
        id: t._id,
        clientName: t.clientName,
        clientDesignation: t.clientDesignation,
        clientCompany: t.clientCompany,
        clientCompanyLogo: t.clientCompanyLogo,
        rating: t.rating,
        review: t.review,
        ...(t.hasProjectLink && t.projectLink && { projectLink: t.projectLink }),
        ...(t.hasProjectLink !== undefined && { hasProjectLink: t.hasProjectLink }),
        ...(t.avatar && { avatar: t.avatar }),
        ...(t.platformLogo && { platformLogo: t.platformLogo }),
    };
};

// Helper function to convert database testimonial to TestimonialCard format
const convertDbToCardFormat = (t: DbTestimonial) => {
    return {
        description: t.review,
        author: t.clientName,
        rating: t.rating,
        avatar: t.avatar,
        logo: t.clientCompanyLogo,
        projectHref: t.hasProjectLink && t.projectLink ? t.projectLink : undefined,
        hasProjectLink: t.hasProjectLink ?? false,
    };
};

export default function TestimonialPage() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTestimonial, setActiveTestimonial] = useState<ExtendedTestimonial | null>(null);
    const [testimonials, setTestimonials] = useState<DbTestimonial[]>([]);
    const [featuredTestimonial, setFeaturedTestimonial] = useState<DbTestimonial | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch all testimonials
                const [allRes, featuredRes] = await Promise.all([
                    fetch('/api/testimonials?limit=100'),
                    fetch('/api/testimonials?featured=true&limit=1'),
                ]);

                if (!allRes.ok || !featuredRes.ok) {
                    throw new Error('Failed to fetch testimonials');
                }

                const allData = await allRes.json();
                const featuredData = await featuredRes.json();

                setTestimonials(allData.testimonials || []);
                setFeaturedTestimonial(featuredData.testimonial || null);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load testimonials');
                console.error('Error fetching testimonials:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, []);

    const openModal = useCallback((testimonial: DbTestimonial) => {
        const extendedTestimonial = convertDbToExtendedTestimonial(testimonial);
        setActiveTestimonial(extendedTestimonial);
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setActiveTestimonial(null);
    }, []);



    if (loading) {
        return (
            <main className="xl:mt-32 mt-16 pb-16 md:pb-20 lg:pb-24 xl:pb-32 overflow-hidden">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading testimonials...</p>
                    </div>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="xl:mt-32 mt-16 pb-16 md:pb-20 lg:pb-24 xl:pb-32 overflow-hidden">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <p className="text-red-600 mb-4">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </main>
        );
    }

    // Filter out featured testimonial from the list
    const regularTestimonials = featuredTestimonial
        ? testimonials.filter(t => t._id !== featuredTestimonial._id)
        : testimonials;

    return (
        <main className="xl:mt-32 mt-16 pb-16 md:pb-20 lg:pb-24 xl:pb-32 overflow-hidden" >
            {/* Hero Section with Main Heading and Featured Testimonial */}
            {featuredTestimonial && (
                <section className=" relative overflow-hidden mb-12 xl:mb-0 ">
                    <div className="custom-container mx-auto ">
                        <div className="grid grid-cols-1 xl:grid-cols-12 gap- lg:gap-16 items-start">
                            {/* Main Heading - Left Side */}
                            <div className="flex flex-col justify-center space-y-6 self-center xl:col-span-5">
                                

                              

                                <h1 className="tracking-tight leading-tight mb-0 pb-0">
                                    <span className="block text-2xl lg:text-4xl font-semibold text-[#1b1b1b]">
                                        Customers love our team
                                    </span>
                                    <span className="block text-3xl sm:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-[#272d57]">
                                        So will you.
                                    </span>
                                </h1>
                            </div>

                            {/* Featured Testimonial - Right Side */}
                            <div className="relative w-full xl:col-span-7 mt-6 lg:mt-0">
                                {/* Diagonal background */}
                                <div className="relative section-background-filler transform 2xl:-skew-x-24 p-8 lg:p-12 w-full">
                                    <div className="transform 2xl:skew-x-24">

                                        {/* Top row: Avatar + Quote/Description */}
                                        <div className="flex flex-col xl:flex-row justify-between items-start gap-6 mb-6 m-4">

                                            {/* Avatar */}
                                            <div className="w-24 h-24  rounded-full overflow-hidden flex-shrink-0  xl:mx-0 order-[-1] xl:order-1 mb-4 xl:mb-0">
                                                <Image
                                                    src={featuredTestimonial.avatar || "/test-images/default-avtar.png"}
                                                    alt={featuredTestimonial.clientName}
                                                    width={128}
                                                    height={128}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Description */}
                                            <div className="flex-1 2xl:pr-6">
                                                <p className="xl:ml-6 text-base 2xl:text-lg text-[#1b1b1b]/80 leading-relaxed 2xl:max-w-2xl">
                                                    {featuredTestimonial.review}
                                                </p>
                                            </div>

                                        </div>

                                        {/* Bottom row: Rating, Author, Action Links */}
                                        <div className="flex flex-col md:flex-row md:items-end md:justify-between space-y-4 md:space-y-0">

                                            {/* Rating */}
                                            <div className="flex flex-col space-y-2">
                                                <div className="flex items-center space-x-3">
                                                    <span className="text-lg font-semibold text-gray-800">
                                                        {featuredTestimonial.rating} / 5
                                                    </span>
                                                    <div className="flex space-x-1 text-orange-400">
                                                        {[...Array(Math.floor(featuredTestimonial.rating))].map((_, i) => (
                                                            <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                            </svg>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="text-lg font-bold text-gray-900">
                                                    {featuredTestimonial.clientName}
                                                </div>
                                            </div>

                                            {/* Action Links */}
                                            <div>
                                                <div className="flex items-center space-x-4 md:mr-16">
                                                    <button
                                                        onClick={() => openModal(featuredTestimonial)}
                                                        className="text-red-600 hover:text-[#d53333] font-medium text-base cursor-pointer transition-colors"
                                                    >
                                                        Read in full
                                                    </button>

                                                    {featuredTestimonial.hasProjectLink && featuredTestimonial.projectLink && (
                                                        <>
                                                            <span className="text-gray-400">/</span>
                                                            <Link
                                                                href={featuredTestimonial.projectLink}
                                                                className="text-red-600 hover:text-[#D53333] font-medium text-base cursor-pointer transition-colors"
                                                            >
                                                                Check the project
                                                            </Link>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                            </div>


                        </div>
                    </div>
                </section>
            )}

            {/* Testimonial Grid Section */}
            <section className=" relative">
                <div className="custom-container mx-auto px-4 sm:px-6 lg:px-8">
                    {regularTestimonials.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {regularTestimonials.map((testimonial, index) => {
                                const cardData = convertDbToCardFormat(testimonial);
                                return (
                                    <div key={testimonial._id}>
                                        <TestimonialCard
                                            description={cardData.description}
                                            author={cardData.author}
                                            rating={cardData.rating}
                                            avatar={cardData.avatar || "/test-images/default-avtar.png"}
                                            showBottomBorder={index < 3}
                                            mounted={true}
                                            onReadFull={() => openModal(testimonial)}
                                            {...(cardData.projectHref && { projectHref: cardData.projectHref })}
                                            hasProjectLink={cardData.hasProjectLink}
                                            {...(cardData.logo && { logo: cardData.logo })}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-gray-600">No testimonials available at the moment.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* New Testimonial Modal */}
            <TestimonialModal
                testimonial={activeTestimonial}
                isOpen={isModalOpen}
                onClose={closeModal}
            />

        </main>
    );
}


